/**
 * Real-time Session Logging Initialization
 * This script should be loaded early in your app to start capturing all logs from the beginning
 */

import { initializeSessionManager } from './sessionManager.js';
// eslint-disable-next-line no-unused-vars
import { initializeFrontendLogCapture } from './frontendLogCapture.js';

// Initialize logging system
console.log('🚀 Initializing Alpha Assist real-time logging system...');

// Check if we're in a browser environment
if (typeof window !== 'undefined') {
    // Initialize session manager (which will also initialize log capture)
    const sessionManager = initializeSessionManager();

    // Make utilities globally available for easy access
    window.sessionManager = sessionManager;
    window.logCapture = window.logCapture || sessionManager.logCapture;

    // Helper functions for manual logging
    window.logUserAction = (action, data) => {
        sessionManager.trackUserAction(action, data);
    };

    window.logError = (error, context) => {
        sessionManager.trackError(error, context);
    };

    window.linkCustomer = (customerData) => {
        sessionManager.linkCustomer(customerData);
    };

    // Enhanced console logging with session context
    const originalError = console.error;
    console.error = (...args) => {
        // Let the log capture system handle it first
        originalError(...args);

        // Also explicitly track as an error if it looks like an actual error
        if (args[0] instanceof Error || (typeof args[0] === 'string' && args[0].toLowerCase().includes('error'))) {
            sessionManager.trackError(args[0], { args: args.slice(1) });
        }
    };

    // Track common user interactions automatically
    document.addEventListener('click', (event) => {
        // Track clicks on important elements
        const target = event.target;
        if (target.tagName === 'BUTTON' || target.tagName === 'A' || target.hasAttribute('data-track')) {
            const action = target.getAttribute('data-action') ||
                target.textContent?.trim() ||
                `${target.tagName.toLowerCase()}_click`;

            sessionManager.trackUserAction(`click_${action.toLowerCase().replace(/\s+/g, '_')}`, {
                element: target.tagName,
                text: target.textContent?.trim(),
                href: target.href,
                id: target.id,
                className: target.className
            });
        }
    });

    // Track form submissions
    document.addEventListener('submit', (event) => {
        const form = event.target;
        if (form.tagName === 'FORM') {
            const formData = new FormData(form);
            const data = {};
            for (let [key, value] of formData.entries()) {
                // Don't log sensitive data
                if (!key.toLowerCase().includes('password') && !key.toLowerCase().includes('secret')) {
                    data[key] = value;
                }
            }

            sessionManager.trackUserAction('form_submit', {
                formId: form.id,
                formAction: form.action,
                formMethod: form.method,
                fieldCount: Object.keys(data).length,
                fields: Object.keys(data)
            });
        }
    });

    // Track input focus (to see what users are interacting with)
    document.addEventListener('focusin', (event) => {
        const target = event.target;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
            sessionManager.trackUserAction('input_focus', {
                element: target.tagName,
                type: target.type,
                name: target.name,
                id: target.id,
                placeholder: target.placeholder
            });
        }
    });

    // Smart session state tracking - only log meaningful changes
    let lastSessionSnapshot = null;

    window.logSessionProgression = (currentPage) => {
        const currentSession = window.getSessionData();
        if (!currentSession) return;

        // Compare with last snapshot and log meaningful changes
        if (lastSessionSnapshot) {
            const changes = detectMeaningfulChanges(lastSessionSnapshot, currentSession, currentPage);
            changes.forEach(change => {
                window.logUserAction(change);
            });
        }

        // Store current state for next comparison
        lastSessionSnapshot = JSON.parse(JSON.stringify(currentSession));
    };

    function detectMeaningfulChanges(previous, current, currentPage) {
        const changes = [];

        // Customer data changes
        if (!previous.customerData && current.customerData) {
            const customer = current.customerData;
            changes.push(`Customer Profile Completed: ${customer.firstName} ${customer.lastName} (${customer.email})`);
        } else if (previous.customerData && current.customerData) {
            // Check for significant customer data updates
            if (previous.customerData.firstName !== current.customerData.firstName ||
                previous.customerData.lastName !== current.customerData.lastName) {
                changes.push(`Customer Name Updated: ${current.customerData.firstName} ${current.customerData.lastName}`);
            }
        }

        // Booking selections
        if (!previous.bookingData && current.bookingData) {
            changes.push('Started Service Configuration');
        }

        if (previous.bookingData && current.bookingData) {
            if (previous.bookingData.serviceType !== current.bookingData.serviceType) {
                changes.push(`Service Type Selected: ${current.bookingData.serviceType}`);
            }
            if (previous.bookingData.productType !== current.bookingData.productType) {
                changes.push(`Product Type Selected: ${current.bookingData.productType}`);
            }
            if (previous.bookingData.jobDescription !== current.bookingData.jobDescription && current.bookingData.jobDescription) {
                changes.push(`Job Description Added: ${current.bookingData.jobDescription.length} characters`);
            }
        }

        // Page progression
        if (previous.currentStep !== current.currentStep) {
            changes.push(`Page Progression: ${previous.currentStep || 'start'} → ${current.currentStep}`);
        }

        return changes;
    }

    // Auto-trigger on page visibility changes (page transitions)
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            setTimeout(() => window.logSessionProgression(window.location.pathname), 500);
        }
    });

    // Expose session info for debugging
    window.getSessionInfo = () => sessionManager.getSessionInfo();

    // Expose full session data including form data
    window.getSessionData = () => {
        try {
            const sessionData = localStorage.getItem('alpha_air_booking_session');
            if (sessionData) {
                return JSON.parse(sessionData);
            }
            return null;
        } catch (error) {
            console.error('Error reading session data:', error);
            return null;
        }
    };

    // Helper to view current form state
    window.showSessionState = () => {
        console.log('=== SESSION STATE ===');
        console.log('Basic Info:', window.getSessionInfo());
        console.log('Full Data:', window.getSessionData());
        console.log('==================');
    };

    // Manual trigger for testing progression logging
    window.testProgression = () => {
        console.log('🧪 Testing session progression...');
        window.logSessionProgression(window.location.pathname);
    };

    console.log('✅ Real-time logging system initialized successfully');
    console.log('📊 Session ID:', sessionManager.getSessionId());
    console.log('🔧 Available global functions: linkCustomer(), logUserAction(), logError(), getSessionInfo(), getSessionData(), showSessionState(), testProgression()');

} else {
    console.warn('Real-time logging system can only be initialized in browser environment');
}